<template>
  <o-modal
    ref="modal"
    :title="$t('components.customer.actions.deleteCustomer.title')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', disabled: isDisabled, callback: handleConfirm },
    ]"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full flex flex-col"
  >
    <div class="text-left h-full">
      <o-scroll class="h-full">
        <div class="pb-4 border-b text-center">
          <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
            {{ $t('components.customer.actions.deleteCustomer.title') }}
          </h3>

          <p class="mb-4 text-sm leading-5 text-gray-600 dark:text-gray-300" v-html="$t('components.customer.actions.deleteCustomer.info', { name: customer.displayName, id: customer.idCustomer })" />
        </div>

        <o-alert :title="$t('components.customer.actions.deleteCustomer.warning')" variant="danger" show class="mb-6 text-sm">
          <p class="text-sm">
            {{ $t('components.customer.actions.deleteCustomer.warningInfo') }}
          </p>
        </o-alert>

        <div class="text-sm text-gray-700 dark:text-gray-200 mb-6">
          <p class="pb-3" v-html="$t('components.customer.actions.deleteCustomer.exportCustomerData')" />

          <p class="pb-3" v-html="$t('components.customer.actions.deleteCustomer.archiveCustomer')" />

          <p v-html="$t('components.customer.actions.deleteCustomer.deleted')" />
          <ul class="list-disc list-outside ml-5 mb-3" v-html="$t('components.customer.actions.deleteCustomer.deletedInfo')" />

          <p v-html="$t('components.customer.actions.deleteCustomer.delinked')" />
          <ul class="list-disc list-outside ml-5" v-html="$t('components.customer.actions.deleteCustomer.delinkedInfo')" />
        </div>

        <div class="mb-6 mx-2">
          <o-field-group rules="required">
            <checkbox-field v-model="confirmValue" :label="$t('general.confirm')" :info="$t('general.readAndUnderstand')" label-class="text-sm ml-3 text-gray-700 dark:text-gray-200" />
          </o-field-group>
        </div>
      </o-scroll>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import { OAlert, OFieldGroup } from '@/components/elements'
import { CheckboxField } from '@/components/fields'

import type { Customer } from '~/_api/models/customer'

interface Props {
  customer: Customer
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()
const router = useRouter()

const modal = ref<any>()
const confirmValue = ref(false)

const isDisabled = computed(() => {
  return !confirmValue.value
})

const open = () => {
  confirmValue.value = false
  modal.value.open()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.customer.erase(props.customer.idCustomer.toString())

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.customer.actions.deleteCustomer.success'),
        text: $i18n.t('components.customer.actions.deleteCustomer.successInfo', { name: props.customer.displayName }),
        variant: 'info',
      })

      $bus.emit.deleted('Customer', props.customer)
      router.replace('/customer')
      emit('success')
    }
  } catch (e: any) {
    console.log(e)

    $notification.toast({
      title: $i18n.t('components.customer.actions.deleteCustomer.error'),
      text: $i18n.t('components.customer.actions.deleteCustomer.errorInfo', { name: props.customer.displayName }),
      variant: 'danger',
    })

    emit('error')
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
